import React from 'react';
import LegalPageLayout from './LegalPageLayout';

const TermsOfServicePage: React.FC = () => (
  <LegalPageLayout title="Terms of Service" lastUpdated="June 2026">
    <p>Welcome to SkipScreen.</p>
    <p>
      SkipScreen is a professional portfolio platform that enables candidates to showcase their
      skills, experience, projects, accomplishments, certifications, and screening responses through
      interactive profiles, video content, and communication tools.
    </p>
    <p>By creating an account or using SkipScreen, you agree to the following terms.</p>

    <h2>1. Eligibility</h2>
    <p>
      You must be at least 18 years old or the age of majority in your jurisdiction to use
      SkipScreen.
    </p>
    <p>
      You are responsible for ensuring that all information you provide is accurate and up to date.
    </p>

    <h2>2. Ownership of Your Content</h2>
    <p>You retain ownership of all content you upload to SkipScreen, including:</p>
    <ul>
      <li>Videos</li>
      <li>Resumes</li>
      <li>Certifications</li>
      <li>Projects</li>
      <li>Accomplishments</li>
      <li>Profile information</li>
      <li>Messages</li>
    </ul>
    <p>
      By uploading content, you grant SkipScreen permission to host, display, and share that content
      only as necessary to provide platform services and facilitate interactions that you authorize.
    </p>

    <h2>3. Candidate Control</h2>
    <p>SkipScreen is built around candidate control.</p>
    <p>You control:</p>
    <ul>
      <li>Who receives access to your profile</li>
      <li>What information is displayed</li>
      <li>Whether your profile is public, private, or invite-only</li>
      <li>Whether recruiter access remains active</li>
      <li>Whether your account remains active</li>
    </ul>
    <p>You may revoke recruiter access or delete your account at any time.</p>

    <h2>4. Recruiter Use</h2>
    <p>
      Recruiters may access candidate information only for legitimate hiring and recruiting
      purposes.
    </p>
    <p>Recruiters may not:</p>
    <ul>
      <li>Copy candidate information for resale</li>
      <li>Scrape or automate profile collection</li>
      <li>Redistribute candidate information</li>
      <li>Use platform data for marketing purposes</li>
      <li>Misrepresent employment opportunities</li>
    </ul>
    <p>Violation may result in immediate account suspension or termination.</p>

    <h2>5. Prohibited Content</h2>
    <p>Users may not upload:</p>
    <ul>
      <li>False information</li>
      <li>Fraudulent credentials</li>
      <li>Copyrighted materials without permission</li>
      <li>Offensive, abusive, or unlawful content</li>
      <li>Malware or harmful software</li>
    </ul>
    <p>SkipScreen reserves the right to remove content that violates these rules.</p>

    <h2>6. Privacy and Data Security</h2>
    <p>SkipScreen uses industry-standard security measures to protect user information.</p>
    <p>
      Uploaded content may include professional and personal career information. While we work to
      protect user data, no online platform can guarantee absolute security. Users are encouraged to
      use strong passwords and safeguard account credentials.
    </p>

    <h2>7. Analytics and Platform Insights</h2>
    <p>
      SkipScreen may provide profile analytics, engagement metrics, recruiter interactions, and
      other platform insights.
    </p>
    <p>
      These metrics are intended for informational purposes and may not always reflect complete user
      activity.
    </p>

    <h2>8. AI Features and Generated Content</h2>
    <p>
      SkipScreen may provide AI-powered features, recommendations, generated screening prompts,
      profile insights, trend information, negotiation guidance, summaries, ratings, or other
      automated content.
    </p>
    <p>
      AI-generated content is provided for informational and convenience purposes only and may
      contain inaccuracies, omissions, or outdated information.
    </p>
    <p>
      Users are responsible for independently reviewing and verifying all AI-generated content
      before relying upon it for employment, recruiting, negotiation, career, or business decisions.
    </p>
    <p>
      SkipScreen does not guarantee the accuracy, completeness, reliability, suitability, or
      availability of AI-generated outputs.
    </p>
    <p>
      Users acknowledge that employment decisions should not be based solely on AI-generated
      information.
    </p>

    <h2>9. Responsible Use of AI</h2>
    <p>Users may not use SkipScreen&apos;s AI features to:</p>
    <ul>
      <li>Generate unlawful, discriminatory, or harmful content</li>
      <li>Impersonate another person</li>
      <li>Misrepresent qualifications, credentials, or experience</li>
      <li>Create fraudulent or deceptive employment materials</li>
      <li>Circumvent hiring laws or regulations</li>
      <li>Manipulate ratings, reviews, analytics, or platform insights</li>
    </ul>
    <p>
      SkipScreen reserves the right to restrict, suspend, or terminate access to AI-powered features
      if misuse is detected.
    </p>

    <h2>10. User Content Accuracy</h2>
    <p>
      Candidates are solely responsible for the accuracy of information submitted to SkipScreen.
    </p>
    <p>
      Users may not knowingly provide false, misleading, fabricated, or materially inaccurate
      information regarding:
    </p>
    <ul>
      <li>Employment history</li>
      <li>Education</li>
      <li>Certifications</li>
      <li>Professional qualifications</li>
      <li>References</li>
      <li>Work experience</li>
      <li>Interview responses</li>
      <li>Portfolio materials</li>
    </ul>
    <p>
      SkipScreen may remove content or suspend accounts that are found to contain fraudulent or
      intentionally misleading information.
    </p>

    <h2>11. Automated Decisions and Hiring Responsibility</h2>
    <p>SkipScreen does not make hiring decisions on behalf of employers.</p>
    <p>
      Any analytics, recommendations, ratings, insights, AI outputs, trend information, or
      platform-generated content are advisory only.
    </p>
    <p>
      Recruiters and employers remain solely responsible for evaluating candidates and complying
      with all applicable employment, hiring, anti-discrimination, privacy, and workplace laws.
    </p>
    <p>
      SkipScreen does not evaluate, rank, recommend, endorse, certify, or select candidates for
      employment.
    </p>
    <p>
      All recruiting, interviewing, and hiring decisions remain solely the responsibility of
      employers, recruiters, and candidates.
    </p>

    <h2>12. No Employment Guarantee</h2>
    <p>SkipScreen does not guarantee:</p>
    <ul>
      <li>Employment</li>
      <li>Interviews</li>
      <li>Recruiter responses</li>
      <li>Job offers</li>
      <li>Hiring outcomes</li>
      <li>Salary outcomes</li>
      <li>Negotiation results</li>
    </ul>
    <p>
      SkipScreen is a platform for professional presentation and interaction. Hiring decisions
      remain solely between candidates and employers.
    </p>

    <h2>13. Account Termination</h2>
    <p>SkipScreen may suspend or terminate accounts that:</p>
    <ul>
      <li>Violate these Terms</li>
      <li>Engage in fraudulent activity</li>
      <li>Abuse platform services</li>
      <li>Attempt unauthorized data collection</li>
    </ul>

    <h2>14. Limitation of Liability</h2>
    <p>
      To the fullest extent permitted by law, SkipScreen shall not be liable for indirect,
      incidental, consequential, special, exemplary, or punitive damages arising from use of the
      platform.
    </p>
    <p>Users use the platform at their own discretion and risk.</p>

    <h2>15. Changes to These Terms</h2>
    <p>
      SkipScreen may update these Terms periodically. Continued use of the platform after updates
      constitutes acceptance of the revised Terms.
    </p>

    <div className="legal-page__contact">
      <h2>16. Contact</h2>
      <p>
        Questions regarding these Terms may be directed to:{' '}
        <a href="mailto:contact@useskipscreen.com">contact@useskipscreen.com</a>
      </p>
      <p>© 2026 SkipScreen LLC. All rights reserved.</p>
    </div>
  </LegalPageLayout>
);

export default TermsOfServicePage;
